import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { debounce } from "debounce";
import { OnbShell, OnbStep, OnbOptions, OnbCountryPicker } from "../Onboarding/OnbKit";
import { DIAL_CODES, toE164 } from "../../Constant/dialCodes";
import { usePhoneOtp, OTP_LENGTH } from "../usePhoneOtp";
import {
  getKnownProfile,
  getStudent,
  hasValue,
  isSignedIn,
  syncProfileFromTest,
} from "../../Services/studentAuth";
import { notifyAuthChange } from "../../utils/sessionUser";
import { searchCountries } from "../../Services/Function";
import "../../Assets/Styles/auth-flow.css";

/**
 * SIGN-IN + FIRST-TIME ONBOARDING — one modal for the whole site.
 *
 * The flow the product wants:
 *   1. Sign in  = phone number → SMS code (Firebase OTP → Ulearn session).
 *   2. EXISTING student (profile already has the common answers) → the modal
 *      simply closes and they carry on.
 *   3. NEW student → one short form right after verification: name, date of
 *      birth, and nationality, saved to the account so nothing asks for them
 *      again (fill-once, utils/onboardingSteps).
 *
 * Opened while ALREADY signed in it skips straight to whatever profile
 * questions are still unanswered (or closes immediately when none are), so
 * the same component also works as a "finish your profile" prompt.
 *
 * Design: the phone + OTP steps keep the ORIGINAL compact sign-in popup (the
 * navbar card with the Ulearn logo and dial-code dropdown), exactly as it
 * looked before; only the first-time profile questions open as the full-screen
 * OnbKit onboarding, so they feel like the first step of the test journey.
 */

/**
 * The questions asked right after OTP verification, in order: the identity
 * basics plus the study plan (destination, what they want to study). Saved to
 * the account so the tests' own onboardings skip them (fill-once).
 */
const PROFILE_FIELDS = ["name", "dob", "nationality", "destination", "degree"];

const COPY = {
  en: {
    phoneTitle: "Sign in to Ulearn",
    phoneHint: "Enter your phone number — we'll send you a one-time code.",
    otpTitle: "Enter the code",
    otpSent: (n) => `We sent a ${n}-digit code by SMS to`,
    change: "← Different number",
    resend: "Resend code",
    resendIn: (s) => `You can resend in ${s}s`,
    verify: "Verify & sign in",
    verifying: "Verifying…",
    send: "Continue",
    sending: "Sending code…",
    continue: "Continue",
    name: { icon: "👋", title: "What's your name?", ph: "Your full name" },
    dob: { icon: "🎂", title: "When were you born?", hint: "We use this to tailor tests and results to your stage." },
    nationality: { icon: "🌍", title: "What's your nationality?" },
    destination: { icon: "✈️", title: "Where do you want to study?" },
    degree: { icon: "🎓", title: "What do you want to study?" },
    destinations: [
      { key: "UK", icon: "🇬🇧", label: "United Kingdom" },
      { key: "US", icon: "🇺🇸", label: "United States" },
      { key: "Australia", icon: "🇦🇺", label: "Australia" },
      { key: "Others", icon: "🌏", label: "Somewhere else" },
    ],
    degrees: [
      { key: "Bachelor's", icon: "🎒", label: "Bachelor's" },
      { key: "Master's", icon: "🧑‍🎓", label: "Master's" },
      { key: "PhD", icon: "🔬", label: "PhD" },
    ],
    saving: "Saving your details…",
  },
  ar: {
    phoneTitle: "تسجيل الدخول إلى يوليرن",
    phoneHint: "أدخل رقم هاتفك — سنرسل لك رمزاً لمرة واحدة.",
    otpTitle: "أدخل الرمز",
    otpSent: (n) => `أرسلنا رمزاً مكوناً من ${n} أرقام برسالة نصية إلى`,
    change: "تغيير الرقم →",
    resend: "إعادة إرسال الرمز",
    resendIn: (s) => `يمكنك إعادة الإرسال بعد ${s} ثانية`,
    verify: "تأكيد وتسجيل الدخول",
    verifying: "جارٍ التحقق…",
    send: "متابعة",
    sending: "جارٍ إرسال الرمز…",
    continue: "متابعة",
    name: { icon: "👋", title: "ما اسمك؟", ph: "اسمك الكامل" },
    dob: { icon: "🎂", title: "ما هو تاريخ ميلادك؟", hint: "نستخدمه لتخصيص الاختبارات والنتائج لمرحلتك." },
    nationality: { icon: "🌍", title: "ما هي جنسيتك؟" },
    destination: { icon: "✈️", title: "في أي بلد ترغب بالدراسة؟" },
    degree: { icon: "🎓", title: "ماذا تريد أن تدرس؟" },
    destinations: [
      { key: "UK", icon: "🇬🇧", label: "بريطانيا" },
      { key: "US", icon: "🇺🇸", label: "أمريكا" },
      { key: "Australia", icon: "🇦🇺", label: "أستراليا" },
      { key: "Others", icon: "🌏", label: "غيرها" },
    ],
    degrees: [
      { key: "Bachelor's", icon: "🎒", label: "البكلوريوس" },
      { key: "Master's", icon: "🧑‍🎓", label: "الماجستير" },
      { key: "PhD", icon: "🔬", label: "الدكتوراة" },
    ],
    saving: "جارٍ حفظ بياناتك…",
  },
};

/** Bilingual labels for the destination/degree keys stored on the profile. */
const AR_NAMES = {
  UK: "بريطانيا", US: "أمريكا", Australia: "أستراليا", Others: "غيرها",
  "Bachelor's": "البكلوريوس", "Master's": "الماجستير", PhD: "الدكتوراة",
};

const RECOMMENDED_COUNTRIES = [
  { english_name: "Kuwait", arabic_name: "الكويت", alpha2_code: "KW", id: "63cfb299df954c47a6fde217" },
  { english_name: "Saudi Arabia", arabic_name: "السعودية", alpha2_code: "SA", id: "63cfb299df954c47a6fde264" },
  { english_name: "Qatar", arabic_name: "قطر", alpha2_code: "QA", id: "63cfb299df954c47a6fde255" },
  { english_name: "Oman", arabic_name: "سلطنة عمان", alpha2_code: "OM", id: "63cfb299df954c47a6fde248" },
  { english_name: "United Arab Emirates", arabic_name: "الإمارات العربية المتحدة", alpha2_code: "AE", id: "63cfb299df954c47a6fde28d" },
];

/** Which of the common questions does this profile still owe an answer to? */
const missingFields = () => {
  const known = getKnownProfile();
  return PROFILE_FIELDS.filter((field) => !hasValue(known[field]));
};

/**
 * Should a "start test" click open this modal first?
 * True for a signed-out visitor AND for a signed-in student whose profile is
 * still missing common answers (they resume at the first unanswered one).
 */
export const needsAuthFlow = () => !isSignedIn() || missingFields().length > 0;

const AuthFlowModal = ({ show = true, language = "ar", onHide, onDone }) => {
  const ar = language === "ar";
  const c = COPY[ar ? "ar" : "en"];

  // "phone" | "otp" | a PROFILE_FIELDS entry | "saving"
  const [stage, setStage] = useState("phone");
  const [navDir, setNavDir] = useState("fwd");
  const [path, setPath] = useState([]); // the profile questions THIS student answers
  const [answers, setAnswers] = useState({});
  const [countryCode, setCountryCode] = useState("+965");
  const [national, setNational] = useState("");
  const [ccOpen, setCcOpen] = useState(false);
  const [ccQuery, setCcQuery] = useState("");
  const [nameText, setNameText] = useState("");
  const [dobText, setDobText] = useState("");
  const [ctySearch, setCtySearch] = useState("");
  const [ctyResults, setCtyResults] = useState([]);
  const [ctyLoading, setCtyLoading] = useState(false);
  const [pickedCty, setPickedCty] = useState(null);
  const doneRef = useRef(false);

  const finish = async (collected) => {
    if (doneRef.current) return;
    doneRef.current = true;
    setStage("saving");
    // fill-empty only, and it never throws — a flaky network can't trap the
    // student inside the modal after they've answered everything
    await syncProfileFromTest(collected);
    notifyAuthChange();
    if (onDone) onDone(getStudent());
    else if (onHide) onHide();
  };

  /** Route to the next unanswered question, or wrap up. */
  const advance = (fromField, collected) => {
    const index = path.indexOf(fromField);
    const next = path[index + 1];
    setNavDir("fwd");
    if (next) {
      setAnswers(collected);
      setStage(next);
    } else {
      finish(collected);
    }
  };

  /**
   * @param {boolean} forceAll - a BRAND-NEW account always answers all three
   *   questions, even when old fill-once blobs on this device could fill
   *   them: the student should confirm their own details once. Known values
   *   prefill the inputs so confirming is one tap per step.
   */
  const startProfile = (forceAll = false) => {
    const known = getKnownProfile();
    const owed = forceAll ? [...PROFILE_FIELDS] : missingFields();
    if (!owed.length) {
      finish({});
      return;
    }
    if (hasValue(known.name)) setNameText(String(known.name));
    const dob = known.dob ? new Date(known.dob) : null;
    if (dob && !Number.isNaN(dob.getTime())) setDobText(dob.toISOString().slice(0, 10));
    setPath(owed);
    setStage(owed[0]);
  };

  const otp = usePhoneOtp({
    language: ar ? "ar" : "en",
    // isNewStudent comes from the ulearn-students exchange: the record was
    // not in the collection (or was empty) before this sign-in
    onVerified: (result) => startProfile(Boolean(result && result.isNewStudent)),
  });

  // Opened while already signed in → no phone step: jump straight to the
  // unanswered questions (new student), or close at once (existing student).
  useEffect(() => {
    if (show && isSignedIn() && stage === "phone") startProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const searchCty = useMemo(
    () =>
      debounce(async (text) => {
        if (!text) { setCtyResults([]); return; }
        setCtyLoading(true);
        try {
          const res = await searchCountries({ text, sortBy: "createdAt:desc", limit: 7 });
          setCtyResults(res?.results || []);
        } catch { setCtyResults([]); }
        setCtyLoading(false);
      }, 400),
    [],
  );

  /* ----------------------------- progress chrome (profile steps only) ---- */
  // The phone/OTP popup shows no progress bar — exactly like the original
  // sign-in popup. The bar only appears over the onboarding questions.
  const profileIdx = path.indexOf(stage);
  const progress =
    stage === "saving" ? 100 :
    path.length > 1 ? Math.max(6, Math.round((Math.max(0, profileIdx) / (path.length - 1)) * 100)) : 100;

  const backTarget = profileIdx > 0 ? path[profileIdx - 1] : null;

  const goBack = () => {
    if (!backTarget) return;
    setNavDir("back");
    setStage(backTarget);
  };

  /* ------------------------------------------------------------ pieces --- */
  const e164 = toE164(countryCode, national);

  const dobOk = (() => {
    if (!dobText) return false;
    const d = new Date(dobText);
    if (Number.isNaN(d.getTime())) return false;
    const age = (Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
    return age >= 8 && age <= 80;
  })();

  const pickOption = (field, key) => {
    const value = field === "degree" || field === "destination" ? key : key;
    advance(field, { ...answers, [field]: value, [`${field}Ar`]: AR_NAMES[key] || key });
  };

  const renderStage = () => {
    if (stage === "name") {
      return (
        <OnbStep key="name" navDir={navDir} icon={c.name.icon} title={c.name.title}>
          <div className="onb2-field">
            <input className="onb2-search onb2-field-input" placeholder={c.name.ph} value={nameText}
              autoComplete="name"
              onChange={(e) => setNameText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && nameText.trim().length >= 2) advance("name", { ...answers, name: nameText.trim() }); }} />
            {nameText.trim().length >= 2 ? (
              <button type="button" className="onb2-continue" onClick={() => advance("name", { ...answers, name: nameText.trim() })}>{c.continue}</button>
            ) : null}
          </div>
        </OnbStep>
      );
    }

    if (stage === "dob") {
      return (
        <OnbStep key="dob" navDir={navDir} icon={c.dob.icon} title={c.dob.title} hint={c.dob.hint}>
          <div className="onb2-field">
            <input type="date" className="onb2-search onb2-field-input af-date" value={dobText}
              max={new Date().toISOString().slice(0, 10)} min="1945-01-01"
              onChange={(e) => setDobText(e.target.value)} />
            {dobOk ? (
              <button type="button" className="onb2-continue" onClick={() => advance("dob", { ...answers, dob: dobText })}>{c.continue}</button>
            ) : null}
          </div>
        </OnbStep>
      );
    }

    if (stage === "nationality") {
      return (
        <OnbStep key="nationality" navDir={navDir} icon={c.nationality.icon} title={c.nationality.title}>
          <OnbCountryPicker
            arabic={ar}
            searchText={ctySearch}
            onSearchInput={(e) => { setCtySearch(e.target.value); searchCty(e.target.value); }}
            countries={ctyResults}
            loading={ctyLoading}
            recommended={RECOMMENDED_COUNTRIES}
            selectedId={pickedCty?.id}
            onPick={(country) => {
              setPickedCty(country);
              setCtySearch(""); setCtyResults([]);
              advance("nationality", { ...answers, nationality: country.english_name });
            }}
          />
        </OnbStep>
      );
    }

    if (stage === "destination" || stage === "degree") {
      const options = (stage === "destination" ? c.destinations : c.degrees).map((o) => ({
        ...o, onPick: () => pickOption(stage, o.key),
      }));
      const meta = c[stage];
      return (
        <OnbStep key={stage} navDir={navDir} icon={meta.icon} title={meta.title}>
          <OnbOptions options={options} selectedKey={answers[stage]} cols={stage === "degree" ? 3 : undefined} />
        </OnbStep>
      );
    }

    // saving
    return (
      <OnbStep key="saving" navDir={navDir} icon="✅" title={c.saving}>
        <div className="af-spinner" aria-hidden="true" />
      </OnbStep>
    );
  };

  /* ------------------------------------------------- dial-code dropdown -- */
  const dial = DIAL_CODES.find((d) => d.code === countryCode) || DIAL_CODES[0];
  const q = ccQuery.trim().toLowerCase();
  const ccList = DIAL_CODES.filter(
    (d) => !q || d.name.toLowerCase().includes(q) || d.ar.includes(ccQuery.trim()) ||
      d.code.replace("+", "").includes(q.replace("+", "")),
  );
  const pickDial = (d) => { setCountryCode(d.code); setCcOpen(false); setCcQuery(""); };

  /* -------------------------------------------------------------- render -- */
  // Phone + OTP keep the original compact sign-in popup; the profile
  // questions (and the saving splash) open the full-screen onboarding.
  if (stage === "phone" || stage === "otp") {
    return (
      <Modal show={show} centered onHide={onHide} aria-labelledby="afp-title"
        dialogClassName="afp-dialog" contentClassName="afp-content" backdropClassName="afp-backdrop">
        <div className="afp" dir={ar ? "rtl" : "ltr"} lang={ar ? "ar" : "en"}>
          {/* Firebase's invisible reCAPTCHA needs a host node */}
          <div ref={otp.recaptchaHostRef} />
          <button type="button" className="afp-x" onClick={onHide} aria-label={ar ? "إغلاق" : "Close"}>✕</button>
          <img className="afp-logo" src="/design/landing/28bd96ad-1926-4050-8f5a-1f9dc3caee8e.svg" alt="Ulearn" />
          {stage === "phone" ? (
            <form onSubmit={(e) => {
              e.preventDefault();
              if (otp.busy) return;
              // sendCode resolves true once Firebase accepts the number —
              // only then does the popup advance to the code boxes
              otp.sendCode(e164).then((ok) => { if (ok) setStage("otp"); });
            }}>
              <h3 id="afp-title">{c.phoneTitle}</h3>
              <p>{c.phoneHint}</p>
              <div className="afp-phone-row">
                <button type="button" className="afp-cc" aria-haspopup="listbox" aria-expanded={ccOpen}
                  onClick={() => setCcOpen((o) => !o)}>
                  {dial.flag} {dial.code} <span className="afp-cc-caret" aria-hidden="true">▾</span>
                </button>
                <input type="tel" autoFocus placeholder={ar ? "رقم الهاتف" : "Phone number"} value={national}
                  onChange={(e) => { setNational(e.target.value); otp.setError(""); }} />
                {ccOpen && (
                  <>
                    <div className="afp-cc-backdrop" onClick={() => setCcOpen(false)} />
                    <div className="afp-cc-panel" role="listbox">
                      <input className="afp-cc-search" autoFocus
                        placeholder={ar ? "ابحث عن الدولة أو الرمز" : "Search country or code"}
                        value={ccQuery} onChange={(e) => setCcQuery(e.target.value)} />
                      <div className="afp-cc-list">
                        {ccList.map((d) => (
                          <button type="button" key={d.code + d.name} role="option" aria-selected={d.code === dial.code}
                            className={"afp-cc-item" + (d.code === dial.code ? " afp-cc-item--on" : "")}
                            onClick={() => pickDial(d)}>
                            <span className="afp-cc-item-flag">{d.flag}</span>
                            <span className="afp-cc-item-name">{ar ? d.ar : d.name}</span>
                            <span className="afp-cc-item-code">{d.code}</span>
                          </button>
                        ))}
                        {ccList.length === 0 && <div className="afp-cc-empty">{ar ? "لا نتائج" : "No matches"}</div>}
                      </div>
                    </div>
                  </>
                )}
              </div>
              {otp.error && <div className="afp-err">{otp.error}</div>}
              {/* enabled like the original popup — a short number gets the
                  field-level error from sendCode, not a greyed-out button */}
              <button className="afp-cta" type="submit" disabled={otp.busy}>
                {otp.busy ? c.sending : c.send}
              </button>
            </form>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); if (!otp.busy) otp.verifyCode(); }}>
              <h3 id="afp-title">{c.otpTitle}</h3>
              <p>{c.otpSent(OTP_LENGTH)} <bdi dir="ltr" className="afp-number">{e164}</bdi></p>
              <div className="afp-otp-row" role="group" aria-label={otp.t.otpLabel} onPaste={otp.handleOtpPaste}>
                {Array.from({ length: OTP_LENGTH }).map((_, i) => {
                  const ch = otp.otp[i] && otp.otp[i] !== " " ? otp.otp[i] : "";
                  return (
                    <input key={i}
                      ref={(node) => { otp.otpBoxesRef.current[i] = node; }}
                      className={"afp-otp-box" + (ch ? " afp-otp-box--filled" : "")}
                      value={ch} inputMode="numeric" autoComplete={i === 0 ? "one-time-code" : "off"}
                      aria-label={(i + 1) + "/" + OTP_LENGTH}
                      onChange={otp.handleOtpChange(i)} onKeyDown={otp.handleOtpKeyDown(i)} />
                  );
                })}
              </div>
              {otp.notice && <div className="afp-note">{otp.notice}</div>}
              {otp.error && <div className="afp-err">{otp.error}</div>}
              <button className="afp-cta" type="submit" disabled={otp.busy}>
                {otp.busy ? c.verifying : c.verify}
              </button>
              {otp.cooldown > 0 ? (
                <div className="afp-note">{c.resendIn(otp.cooldown)}</div>
              ) : (
                <button type="button" className="afp-back" disabled={otp.busy}
                  onClick={() => otp.sendCode(e164, true)}>{c.resend}</button>
              )}
              <button type="button" className="afp-back" onClick={() => { otp.reset(); setStage("phone"); }}>{c.change}</button>
            </form>
          )}
        </div>
      </Modal>
    );
  }

  // Signed in — the first-time onboarding questions, full-screen.
  return (
    <Modal show={show} fullscreen contentClassName="onb2-content" aria-labelledby="onb2-question" centered onHide={onHide}>
      <OnbShell
        arabic={ar}
        stepIdx={Math.max(1, path.indexOf(stage) + 1)}
        stepTotal={Math.max(1, path.length)}
        progress={progress}
        showBack={Boolean(backTarget)}
        onBack={goBack}
        onClose={onHide}
      >
        {renderStage()}
      </OnbShell>
    </Modal>
  );
};

export default AuthFlowModal;
